/**
 * Portfolio screen — a virtualized, server-paginated table of the organization's
 * modeled loans, above an org-level stat band, with loan-id search + readiness
 * filters. A development/QA window into the uploaded portfolio and a proof of
 * concept for visualizing large loan books.
 *
 * This module is the PURE presentation (header + stats + toolbar + table +
 * empty/error states from props) plus the shared view-state type — deliberately
 * free of the server-state seam so it stories without a router or query. The
 * connected component that drives the queries lives in `PortfolioPageConnected`.
 */
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  PortfolioLoanRow,
  PortfolioSummary,
  PortfolioSortKey,
  PortfolioSortDirection,
} from '@repo/shared/common';
import { PageHeader, Stack, Group, Reveal, Alert, EmptyState, Button } from '~/components/ui';
import { AlertCircle, ChartLineUp, Search, Upload } from '~/components/icons';
import { PortfolioStats } from './PortfolioStats';
import { PortfolioToolbar } from './PortfolioToolbar';
import { PortfolioTable } from './PortfolioTable';
import { ReadinessLegend } from './columns';

/** The URL-backed portfolio view state. Field/readiness slicing lives in the Segment
 *  Builder (the segment store + server draft), NOT here — only loan-id search + sort. */
export interface PortfolioViewState {
  readonly search: string;
  readonly sort: PortfolioSortKey;
  readonly direction: PortfolioSortDirection;
}

/** The view state of a fresh portfolio (newest loans first). */
export const DEFAULT_PORTFOLIO_VIEW: PortfolioViewState = {
  search: '',
  sort: 'loan_id',
  direction: 'desc',
};

export interface PortfolioPagePresentationProps {
  readonly state: PortfolioViewState;
  /** Whether the active segment constrains the portfolio — drives the empty-state copy. */
  readonly segmentActive: boolean;
  readonly summary?: PortfolioSummary | undefined;
  readonly summaryLoading: boolean;
  readonly rows: readonly PortfolioLoanRow[];
  readonly tableLoading: boolean;
  readonly isFetchingNextPage: boolean;
  readonly isError: boolean;
  readonly onReachEnd: () => void;
  readonly onSearchChange: (value: string) => void;
  /** Clear everything: the loan-id search AND the active segment. */
  readonly onClear: () => void;
  readonly onSortChange: (sort: PortfolioSortKey, direction: PortfolioSortDirection) => void;
  readonly onNewUpload?: () => void;
}

export function PortfolioPagePresentation({
  state,
  segmentActive,
  summary,
  summaryLoading,
  rows,
  tableLoading,
  isFetchingNextPage,
  isError,
  onReachEnd,
  onSearchChange,
  onClear,
  onSortChange,
  onNewUpload,
}: PortfolioPagePresentationProps): ReactNode {
  const { t } = useTranslation();
  const filtered = state.search.trim() !== '' || segmentActive;

  const emptyState = filtered ? (
    <EmptyState
      icon={<Search size={44} />}
      title={t('portfolio.empty.noMatchesTitle')}
      description={t('portfolio.empty.noMatchesBody')}
      actions={
        <Button variant="light" onClick={onClear} data-testid="portfolio-empty-clear">
          {t('portfolio.toolbar.clear')}
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<ChartLineUp size={44} />}
      title={t('portfolio.empty.noDataTitle')}
      description={t('portfolio.empty.noDataBody')}
      actions={
        onNewUpload ? (
          <Button
            variant="primary"
            leftSection={<Upload size={16} />}
            onClick={onNewUpload}
            data-testid="portfolio-empty-upload"
          >
            {t('portfolio.empty.upload')}
          </Button>
        ) : null
      }
    />
  );

  return (
    <Stack gap="xl" data-testid="portfolio-page">
      <Reveal>
        <PageHeader
          title={t('portfolio.title')}
          subtitle={t('portfolio.subtitle')}
          titleVariant="display"
        />
      </Reveal>

      <Reveal delay={60}>
        <PortfolioStats summary={summary} isLoading={summaryLoading} />
      </Reveal>

      <Reveal delay={120}>
        <Stack gap="md">
          <PortfolioToolbar search={state.search} onSearchChange={onSearchChange} />

          {isError ? (
            <Alert variant="error" icon={<AlertCircle size={16} />} data-testid="portfolio-error">
              {t('portfolio.loadError')}
            </Alert>
          ) : (
            <Stack gap="xs">
              <Group justify="flex-end">
                <ReadinessLegend />
              </Group>
              <PortfolioTable
                rows={rows}
                isLoading={tableLoading}
                isFetchingNextPage={isFetchingNextPage}
                onReachEnd={onReachEnd}
                sort={state.sort}
                direction={state.direction}
                onSortChange={onSortChange}
                emptyState={emptyState}
                ariaLabel={t('portfolio.tableLabel')}
              />
            </Stack>
          )}
        </Stack>
      </Reveal>
    </Stack>
  );
}
